import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {LazyItemPicker} from '@github-ui/item-picker-base/LazyItemPicker'
import {useItemPickerErrorFallback} from '@github-ui/item-picker-base/useItemPickerErrorFallback'
import {IssueTypePickerBase, type IssueTypePickerBaseProps} from '@github-ui/item-picker-issue-type'
import {QueryClient, useQueryClient, type QueryPersister} from '@github-ui/react-query'
import {useEffect, useRef, useState, useCallback} from 'react'
import {
  apiGetSearchIssueTypes,
  buildItemPickerIssueTypeSearchQueryKey,
  transformSearchIssueTypeResponseToQueryData,
  type IssueTypePickerQueryData,
} from './api/api-get-search-issue-types'
import {parseGraphQLResponse} from '@github-ui/item-picker-base/api/graphql'
import {useAnalytics} from '@github-ui/use-analytics'
import {createIssueTypePersister} from './api/issue-type-persister'

type IssueTypeQueryKey = ReturnType<typeof buildItemPickerIssueTypeSearchQueryKey>

//
// ItemPicker <== ItemPickerWrapper (internal) <== ItemPickerFetcher (internal) <== IssueTypePicker (exported)
//                                                                              <== BulkIssueIssueTypePicker (exported)

export async function fetchIssueTypes(
  queryClient: QueryClient,
  {
    owner,
    repo,
    query,
    count,
    persister,
  }: {
    owner: string
    repo: string
    query: string
    count?: number
    persister?: QueryPersister<IssueTypePickerQueryData, IssueTypeQueryKey>
  },
): Promise<IssueTypePickerQueryData> {
  const response = await queryClient.fetchQuery({
    queryKey: buildItemPickerIssueTypeSearchQueryKey({owner, repo, query, count}),
    queryFn: async () => {
      const responseJson = await apiGetSearchIssueTypes({owner, repo, query, count})
      const responseData = parseGraphQLResponse(responseJson)
      return transformSearchIssueTypeResponseToQueryData(responseData.data)
    },
    persister,
  })
  return response
}

export type IssueTypePickerProps = IssueTypePickerBaseProps & {
  owner: string
  repo: string
}

function ItemPickerFetcher({owner, repo, ...rest}: IssueTypePickerProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [fetchKey, setFetchKey] = useState(0)
  const [isError, setIsError] = useState(false)
  const [data, setData] = useState<IssueTypePickerQueryData | null>(null)
  const [openCount, setOpenCount] = useState(1) // Start at 1 because triggerOpen=true on first mount
  const queryClient = useQueryClient()
  const {sendAnalyticsEvent} = useAnalytics()

  // Mark performance when component mounts (first open via triggerOpen)
  useEffect(() => {
    performance.mark('issue-type-picker-opened')
  }, [])

  useEffect(() => {
    const fetchInitialIssueTypes = async () => {
      try {
        setIsLoading(true)
        const persister: QueryPersister<IssueTypePickerQueryData, IssueTypeQueryKey> = async (
          queryFn,
          context,
          query,
        ) => {
          const issueTypePersister = createIssueTypePersister(sendAnalyticsEvent)
          return issueTypePersister(queryFn, context, query)
        }

        const response = await fetchIssueTypes(queryClient, {
          owner,
          repo,
          query: '',
          persister,
        })
        setData(response)
        setIsLoading(false)
        setIsError(false)
      } catch {
        setIsError(true)
        setIsLoading(false)
      }
    }
    // This useEffect is run to populate the initial list of issue types to show in the picker.
    // It does not take a query into account and only relies on owner and repo.
    // This useEffect has the `fetchInitialIssueTypes` callback (which has `owner` and `repo`)
    // and `fetchKey` as its keys, so it will only be executed if one of those dependencies changes
    // - not whenever the filter changes.
    fetchInitialIssueTypes()
  }, [fetchKey, owner, repo, queryClient, sendAnalyticsEvent])

  // Measure performance from when picker was opened to when data is rendered
  // This runs every time openCount changes (incremented on each open)
  useEffect(() => {
    if (!isLoading && data && openCount > 0) {
      try {
        performance.mark('issue-type-picker-rendered')
        const measure = performance.measure(
          'issue-type-picker-duration',
          'issue-type-picker-opened',
          'issue-type-picker-rendered',
        )
        sendAnalyticsEvent('issue_type_picker.render_duration', undefined, {
          duration: measure.duration,
          caching_enabled: true,
        })
        // Clean up performance marks
        performance.clearMarks('issue-type-picker-opened')
        performance.clearMarks('issue-type-picker-rendered')
        performance.clearMeasures('issue-type-picker-duration')
      } catch {
        // Ignore errors if performance marks don't exist (e.g., picker opened via keyboard)
      }
    }
  }, [isLoading, data, sendAnalyticsEvent, openCount])

  const anchorRef = useRef<HTMLButtonElement>(null)

  const handleOpen = useCallback(() => {
    performance.mark('issue-type-picker-opened')
    setOpenCount(prev => prev + 1)
  }, [])

  const {createFallbackComponent} = useItemPickerErrorFallback({
    errorMessage: LABELS.cantEditItems('types'),
    anchorElement: anchorProps => rest.anchorElement(anchorProps, anchorRef),
    open: true,
  })

  if (isError) {
    return createFallbackComponent(() => setFetchKey(current => current + 1))
  }

  return <IssueTypePickerBase issueTypes={data?.issueTypes ?? []} isLoading={isLoading} {...rest} onOpen={handleOpen} />
}

export function IssueTypePicker({
  owner,
  repo,
  anchorElement,
  readonly,
  activeIssueType,
  ...rest
}: IssueTypePickerProps) {
  return (
    <LazyItemPicker
      keybindingCommandId="item-pickers:open-issue-type"
      anchorElement={(anchorProps, ref) => anchorElement(anchorProps, ref)}
      createChild={() => (
        <ItemPickerFetcher
          owner={owner}
          repo={repo}
          readonly={readonly}
          activeIssueType={activeIssueType}
          triggerOpen
          anchorElement={anchorElement}
          {...rest}
        />
      )}
      insidePortal={rest.insidePortal}
    />
  )
}

try{ ItemPickerFetcher.displayName ||= 'ItemPickerFetcher' } catch {}
try{ IssueTypePicker.displayName ||= 'IssueTypePicker' } catch {}